<template>
  <div :class="columnClassName + ' component-details'">
    <column-header v-if="!isModal" :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <div class="row">
				<div class="col-xs-12" v-if="!isModal">
          <h3>{{ currcomponent.name }}</h3>
        </div>
        <div class="col-xs-12 component-editor-title" v-else>
          <h4>{{ currcomponent.name }}</h4>
        </div>
      </div>
      <lock-info v-if="locked" :locked="locked" :lockStatus="lockStatus" v-on:forceLock="getComponentLock(true)" />
      <div class="row">
        <div class="col-sm-6 leftcol">
          <h3>{{ _('General') }}</h3>
          <div class="form-group languageversioned">
            <label for="name">{{ _('Name') }}</label>
            <input type="text" class="form-control" id="componentName" v-model="currcomponent.name" :placeholder="_('Enter component name')" />
          </div>
        </div>
        <div class="col-sm-6 rightcol">
          <div class="form-group">
            <h3>&nbsp;</h3>
            <label for="type">{{ _('Type') }}</label>
            <p v-if="currcomponent.type">{{ getComponentName(currcomponent.type) }} ({{ currcomponent.type }})</p>
            <select v-else class="form-control" v-model="currcomponent.type">
              <option v-for="ctype in componentTypes" value="ctype.type">{{ getComponentName(ctype.type) }} ({{ ctype.type }})</option>
            </select>
          </div>
        </div>
      </div>
      <div v-if="componentEditorType">
        <component :is="resolveComponent(componentEditorType)" :baseDomain="baseDomain" :shopId="shopId" :apiKey="apiKey" :settings="settings" v-model="currcomponent.data" :readyForSaving="readyForSaving" :component="currcomponent" :uploadOwnerKey="uploadOwnerKey" ref="componentEditor" v-on:filedata="filedataChanged" v-on:upload="queueUploads($event.files, $event.slot)" v-on:library="chooseFromLibrary($event.slot, $event.options)" v-on:additionalSavingDone="additionalSavingDone"></component>
      </div>
    </div>
    <nav class="save-bar">
      <div class="flex-row space-between">
        <button class="btn btn-danger" v-if="!isModal" v-on:click="remove" type="button" :disabled="!isRemoveButtonEnabled"><i class="fa fa-fw fa-trash"></i><span class="hidden-xs"> {{ _('Delete') }}</span></button>
        <span v-else>&nbsp;</span>
        <div class="button-col">
          <button class="btn btn-default" v-on:click="close" style="margin-right: 5px"><i class="fa fa-fw fa-close"></i><span class="hidden-xs"> {{ _('Close') }}</span></button>
          <button v-if="isModal" class="btn btn-warning" v-on:click="unlinkComponent" style="margin-right: 5px"><i class="fa fa-fw fa-chain-broken"></i> {{ _('Remove from layout') }}</button>
          <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>

import { defineAsyncComponent } from 'vue'
import axios from 'axios'
import dimensions from '~/lib/dimensions'
import componenttypes from '~/lib/componenttypes'
import components from '~/lib/components'
import uploadaware from '~/lib/uploadaware'
import { DraggableMultiSelector } from '#components'

export default defineNuxtComponent({
  setup () {
    let activeType = ''
    let activeComponent = null
    const resolveComponent = (type) => {
      if(activeType != type) {
        activeType = type
        activeComponent = defineAsyncComponent(() => import(`~/components/componenteditors/${type}.vue`))
      }
      return activeComponent
    }
    return {
      resolveComponent
    }
  },  
  data () {
    return {
      currcomponent: {},
      readyForSaving: false
    }
  },
  mixins: [components, componenttypes, dimensions, uploadaware],
  computed: {
    isModal () {
      return this.component && this.component.initData && this.component.initData.modal
    },
    componentEditorType () {
      switch(this.currcomponent.type) {
        case 'Agenda':
        case 'ButtonMap':
        case 'ClientReceipt':
        case 'Dashboard':
        case 'IFrame':
        case 'GasPump':
        case 'MaxDashboard':
        case 'MaxTool':
        case 'MovieList':
        case 'ProductList':
        case 'CategoryList':
        case 'ProductReservationSchedule':
        case 'ResourceList':
        case 'ResourceReservationSchedule':
        case 'Receipt':
        case 'ReportList':
        case 'Showtimes':
        case 'TicketActivityList':
        case 'Workflow':
          return this.currcomponent.type + 'Editor'
        default:
          return 'NoEditor'
      }
    },
    additionalSavingNeeded () {
      return this.currcomponent.type == 'CategoryList'
    },
    uploadOwner () {
      if (!this.currcomponent || !this.currcomponent.type) {
        return null
      }
      const id = this.currcomponent.id
      const label = this._('Component') + (id ? ' ' + id : '') + (this.currcomponent.name ? ': ' + this.currcomponent.name : '')
      return {
        type: 'component',
        id,
        label,
        open: {
          component: 'ComponentDetails',
          title: label,
          width: 6,
          data: {
            componentId: id
          }
        }
      }
    }
  },
  methods: {
    additionalSavingDone () {
      this.readyForSaving = false
      this.doSave()
    },
    unlinkComponent () {
      this.$emit('unlinkComponent', this.currcomponent.id)
    },
    filedataChanged (data) {
      this.currcomponent.file_id = data.id
      this.currcomponent.file_ext = data.ext
      this.currcomponent.sharedFile = data.sharedFile
    },
    // Puts a file uploaded through the upload queue in its place (lib/uploadaware.js).
    // The component editor (e.g. ClientReceiptEditor) keeps its own copy of the
    // images and reports changes with `filedata`, so it gets the file first.
    applyUploadResult (slot, file) {
      if (!file) {
        return
      }
      const editor = this.$refs.componentEditor
      if (editor && typeof editor.applyUploadResult === 'function') {
        editor.applyUploadResult(slot, file)
        return
      }
      if (slot === 'main') {
        this.currcomponent.file_id = file.id
        this.currcomponent.file_ext = file.ext
      } else {
        if (!Array.isArray(this.currcomponent.sharedFile)) {
          this.currcomponent.sharedFile = []
        }
        if (this.currcomponent.sharedFile.findIndex(i => String(i.id) === String(file.id)) === -1) {
          this.currcomponent.sharedFile.push({ id: file.id })
        }
      }
    },
    getSaveData () {
      const exportFields = [
        'id',
        'name',
        'type',
        'data'
      ]
      const data = exportFields.reduce((a, e) => (a[e] = this.currcomponent[e], a), {})
      data.image_id = this.currcomponent.file_id
      data.image_ids = (this.currcomponent.sharedFile || []).map(f => f.id).join(';;;')
      data.version = this.version
      return data
    },
    async doRemove () {
      let response
      try {
        this.deleted = true
        response = await axios.delete(this.baseDomain + this.shopId + '/components/' + this.currcomponent.id + '.json?apikey=' + this.apiKey)
      } catch(e) {
        this.deleted = false
        return this.showError(this._('Deleting the component failed!'))
      }
      this.showNotification(this._('Component deleted!'))
      this.close()
    },
    async doSave () {
      const data = this.getSaveData()
      let response
      try {
        if (data.id) {
          response = await axios.put(this.baseDomain + this.shopId + '/components/' + data.id + '.json?apikey=' + this.apiKey, data)
        } else {
          delete data.id
          response = await axios.post(this.baseDomain + this.shopId + '/components.json?apikey=' + this.apiKey, data)
        }
      } catch(e) {
        this.saving = false
        this.applyPendingUploads()
        return this.showError(e)
      }
      if (response && response.data && response.data.id) {
        await this.loadComponent(response.data.id)
      }
      this.saving = false
      this.applyPendingUploads()
      this.saved = true
      setTimeout(() => { this.saved = false }, 1000)
      this.showNotification(this._('Changes saved successfully!'))
      this.$emit('save', this.resource)

      let saveShouldClose = this.saveShouldClose
      if (data && data.type === 'ButtonMap') {
        saveShouldClose = true
      }
      if (saveShouldClose) {
        this.close()
      }
    },
    async save () {
      await this.awaitUploadsBeforeSave()
      this.saving = true
      if (this.additionalSavingNeeded) {
        this.readyForSaving = true
      } else {
        this.doSave()
      }
    },
    async reloadOnVersionChange () {
      if (!this.currcomponent || !this.currcomponent.id) {
        return
      }
      await this.loadComponent(this.currcomponent.id)
    },
    loadComponent (id) {
      return new Promise(async (resolve, reject) => {
        this.loading = true
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/components/' + id + '.json?files=true&apikey=' + this.apiKey + this.versionStr)
        } catch (e) {
          this.loading = false
          this.showError(e)
          reject()
          return
        }
        this.currcomponent = response.data
        this.storeState()
        this.loading = false
        this.applyPendingUploads()
        resolve(this.currcomponent)
      })
    }
  },
  components: {
    DraggableMultiSelector,
  },
  created () {
    if (this.component && this.component.initData) {
      this.loadComponent(this.component.initData.componentId)
    }
  },
  async updated () {
    if (this.component && this.component.initData) {
      if (this.currcomponent && this.currcomponent.id && parseInt(this.currcomponent.id, 10) !== parseInt(this.component.initData.componentId, 10)) {
        try {
          await this.loadComponent(this.component.initData.componentId)
        } catch(e) {}
      }
    }
  },
  async mounted () {
    if(!this.events) {
      let events = {}
      events['componentUpdate'] = async (message) => {
        if (message && message.data && this.currcomponent && parseInt(message.data.id, 10) === parseInt(this.currcomponent.id, 10)) {
          if (!this.unsavedChanges) { // Only update if we haven't made any changes
            this.loadComponent(message.data.id)
          }
        }
      }
      this.events = events
    }
    this.enableEventBus()
  }
})
</script>
